/* global window */
import React from 'react';
import { Modal, Button, Form, Checkbox, Select } from 'antd';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import moment from 'moment';
import CalendarButtonList from '../CalendarButtonList/CalendarButtonListComponent';
import WorkingHoursTimePicker from '../TimePicker/WorkingHoursTimePickerComponent';
import TimeSpan from '../TimeSpan/TimeSpanComponent';
import './CalendarSettingsComponent.scss';
import { browserZone, zoneLabel, isSupportedZone, formatZonePreview } from '../../Utility/zonedTime';

const FormItem = Form.Item;
const CheckboxGroup = Checkbox.Group;

function orderedZoneOptions(zoneOptions, preferred) {
  const priority = ({ zone, supported }) => {
    if (!supported) return 2;
    return preferred.includes(zone) ? 0 : 1;
  };
  return [...zoneOptions].sort((a, b) => priority(a) - priority(b));
}

const messages = defineMessages({
  calendarSettings: {
    id: 'CalendarSettings.CalendarSettings',
    defaultMessage: 'Calendar settings',
  },
  selectTime: {
    id: 'CalendarSettings.Message.SelectTime',
    defaultMessage: 'Please Select Time',
  },
  slotDuration: {
    id: 'CalendarSettings.SlotDuration',
    defaultMessage: 'Default duration',
  },
  defaultReminderTime: {
    id: 'CalendarSettings.DefaultReminder',
    defaultMessage: 'Default Reminder Time',
  },
  bufferDuration: {
    id: 'CalendarSettings.BufferDuration',
    defaultMessage: 'Buffer duration',
  },
  integrateCalendars: {
    id: 'CalendarSettings.IntegrateCalendars',
    defaultMessage: 'Integrate calendars',
  },
  workingDays: {
    id: 'CalendarSettings.WorkingDays',
    defaultMessage: 'Working days',
  },
  workingHours: {
    id: 'CalendarSettings.WorkingHours',
    defaultMessage: 'Working Hours',
  },
  starts: {
    id: 'CalendarSettings.Starts',
    defaultMessage: 'Starts',
  },
  ends: {
    id: 'CalendarSettings.Ends',
    defaultMessage: 'Ends',
  },
  monday: {
    id: 'CalendarSettings.Monday',
    defaultMessage: 'Monday',
  },
  tuesday: {
    id: 'CalendarSettings.Tuesday',
    defaultMessage: 'Tuesday',
  },
  wednesday: {
    id: 'CalendarSettings.Wednesday',
    defaultMessage: 'Wednesday',
  },
  thursday: {
    id: 'CalendarSettings.Thursday',
    defaultMessage: 'Thursday',
  },
  friday: {
    id: 'CalendarSettings.Friday',
    defaultMessage: 'Friday',
  },
  saturday: {
    id: 'CalendarSettings.Saturday',
    defaultMessage: 'Saturday',
  },
  sunday: {
    id: 'CalendarSettings.Sunday',
    defaultMessage: 'Sunday',
  },
  sundayLabel: {
    id: 'CalendarSettings.SundayLabel',
    defaultMessage: 'Su',
  },
  mondayLabel: {
    id: 'CalendarSettings.MondayLabel',
    defaultMessage: 'Mo',
  },
  tuesdayLabel: {
    id: 'CalendarSettings.TuesdayLabel',
    defaultMessage: 'Tu',
  },
  wednesdayLabel: {
    id: 'CalendarSettings.WednesdayLabel',
    defaultMessage: 'We',
  },
  thursdayLabel: {
    id: 'CalendarSettings.ThursdayLabel',
    defaultMessage: 'Th',
  },
  fridayLabel: {
    id: 'CalendarSettings.FridayLabel',
    defaultMessage: 'Fr',
  },
  saturdayLabel: {
    id: 'CalendarSettings.SaturdayLabel',
    defaultMessage: 'Sa',
  },
});

function CalendarSettingsComponent(props) {
  const {
    form,
    intl,
    userSettings,
    showModal,
    handleOk,
    visible,
    showTrigger,
    handleCancel,
    externalProviders,
    refreshCalendar,
    openProvider,
    revokeAccess,
    adoptedZone,
    onAdoptZone,
    onChooseZone,
    zoneSelectRef,
    triggerRef,
    saving,
    saveError,
    saveStatus,
    fetchSettings,
    settingsLoadFailed,
    zoneConflict,
    zoneRequired,
  } = props;
  const zoneIds = userSettings.TimeZoneIds || [];
  const zoneOptions = zoneIds.map(zone => ({ zone, supported: isSupportedZone(zone) }));
  const detected = browserZone();
  const suggested = zoneOptions.find(({ zone, supported }) => supported && zone.toLowerCase() === (detected || '').toLowerCase());
  const stored = zoneOptions.find(({ zone }) => zone.toLowerCase() === (userSettings.TimeZone || '').toLowerCase());
  const suggestedZone = suggested && suggested.zone;
  const validStoredZone = stored && stored.supported && stored.zone;
  const selectedZone = adoptedZone || validStoredZone;
  const badStoredZone = !!userSettings.TimeZone && !validStoredZone;
  const days = [
    {
      label: intl.formatMessage({ ...messages.mondayLabel }),
      value: 'Monday',
    },
    {
      label: intl.formatMessage({ ...messages.tuesdayLabel }),
      value: 'Tuesday',
    },
    {
      label: intl.formatMessage({ ...messages.wednesdayLabel }),
      value: 'Wednesday',
    },
    {
      label: intl.formatMessage({ ...messages.thursdayLabel }),
      value: 'Thursday',
    },
    {
      label: intl.formatMessage({ ...messages.fridayLabel }),
      value: 'Friday',
    },
    {
      label: intl.formatMessage({ ...messages.saturdayLabel }),
      value: 'Saturday',
    },
    {
      label: intl.formatMessage({ ...messages.sundayLabel }),
      value: 'Sunday',
    },
  ];

  let startTime = moment('09:00', 'HH:mm');
  let endTime = moment('17:00', 'HH:mm');
  if (!userSettings.HoursAreDefault) {
    if (userSettings.StartingFreeTime) startTime = moment.utc(userSettings.StartingFreeTime, 'HH:mm:ss').local();
    if (userSettings.EndingFreeTime) endTime = moment.utc(userSettings.EndingFreeTime, 'HH:mm:ss').local();
  }
  if (userSettings.TimeZone && userSettings.LocalStartingFreeTime) {
    startTime = moment(userSettings.LocalStartingFreeTime, 'HH:mm:ss');
  }
  if (userSettings.TimeZone && userSettings.LocalEndingFreeTime) {
    endTime = moment(userSettings.LocalEndingFreeTime, 'HH:mm:ss');
  }
  const draftStart = form.getFieldValue && form.getFieldValue('workingHoursStartTime');
  const draftEnd = form.getFieldValue && form.getFieldValue('workingHoursEndTime');
  const preview = selectedZone && formatZonePreview((draftStart || startTime).format('HH:mm'), (draftEnd || endTime).format('HH:mm'), selectedZone);
  const configStartTime = {
    initialValue: startTime,
    rules: [{ required: true, message: <FormattedMessage {...messages.selectTime} /> }],
  };
  const configEndTime = {
    initialValue: endTime,
    rules: [{ required: true, message: <FormattedMessage {...messages.selectTime} /> }],
  };
  return (
    <div>
      {showTrigger && <span ref={triggerRef}><Button type="primary" className="calendar-settings-button" onClick={showModal}>
        <FormattedMessage {...messages.calendarSettings} />
      </Button></span>}
      <div aria-live="polite" className="visually-hidden">{saveStatus}</div>
      <Modal
        title={
          <span className="calendar-settings-title">
            <FormattedMessage {...messages.calendarSettings} />
          </span>
        }
        visible={visible}
        onOk={handleOk}
        onCancel={handleCancel}
        closable={false}
        maskClosable={false}
        keyboard={!saving}
        className="settings-modal"
        okText="Save"
        okButtonProps={{ disabled: saving || zoneRequired }}
        cancelButtonProps={{ disabled: saving }}
        width="585px"
      >
        <Form onSubmit={handleOk} hideRequiredMark>
          {!userSettings.TimeZone && Object.keys(externalProviders || {}).some(key => externalProviders[key] && externalProviders[key].Registered) && <div className="zone-adoption-banner">
            <strong>Keep your working hours right when clocks change</strong>
            <p>Existing appointments won&apos;t move.</p>
            <Button onClick={onChooseZone}>Choose time zone</Button>
          </div>}
          <FormItem label={zoneRequired || badStoredZone ? 'Choose your time zone' : 'Time zone'}>
            {form.getFieldDecorator('timeZone', { initialValue: badStoredZone ? undefined : validStoredZone || suggestedZone, rules: [{ required: zoneRequired, message: 'Choose your time zone' }] })(
              <Select className="working-zone-select" aria-label="Choose your time zone" ref={zoneSelectRef} onChange={onAdoptZone} showSearch optionFilterProp="children">
                {orderedZoneOptions(zoneOptions, [validStoredZone, suggestedZone]).map(({ zone, supported }) => <Select.Option key={zone} value={zone} disabled={!supported} title={!supported ? 'Not available in this browser' : undefined}>{zoneLabel(zone)}</Select.Option>)}
              </Select>
            )}
          </FormItem>
          {badStoredZone && <div role="alert">{stored ? <>This browser can&apos;t use the time zone {userSettings.TimeZone}. Update your browser, or choose another zone to save changes.</> : <>We couldn&apos;t read the saved time zone. Choose it again.</>}</div>}
          {settingsLoadFailed && zoneIds.length === 0 && <div role="alert">We couldn&apos;t load time zones. <Button onClick={fetchSettings}>Retry</Button></div>}
          {suggestedZone && !userSettings.TimeZone && (!adoptedZone || adoptedZone === suggestedZone) && <div>Suggested from this device</div>}
          <FormItem
            style={{
              marginBottom: 24,
              marginTop: 22,
              fontWeight: 500,
              fontFamily: 'Roboto',
            }}
            colon={false}
            label={<FormattedMessage {...messages.integrateCalendars} />}
          >
            <CalendarButtonList
              externalProviders={externalProviders}
              openProvider={openProvider}
              revokeAccess={revokeAccess}
              refreshCalendar={refreshCalendar}
            />
          </FormItem>

          <FormItem
            style={{ marginBottom: 24, fontWeight: 500, fontFamily: 'Roboto' }}
            colon={false}
            label={<FormattedMessage {...messages.workingDays} />}
          >
            {form.getFieldDecorator('days', {
              initialValue: userSettings.AvailableWeekDays,
            })(<CheckboxGroup options={days} className="week-days-check-box" />)}
          </FormItem>

          <div className="start-working-hours">
            <FormItem
              label={<FormattedMessage {...messages.workingHours} />}
              colon={false}
              style={{
                marginBottom: 24,
                fontWeight: 500,
                fontFamily: 'Roboto',
              }}
            >
              <div className="starts-label">
                <FormattedMessage {...messages.starts} /> &nbsp;
              </div>
              <WorkingHoursTimePicker
                getFieldDecorator={form.getFieldDecorator}
                configTime={configStartTime}
                valueText="workingHoursStartTime"
              />
            </FormItem>
          </div>

          <div className="end-working-hours">
            <FormItem
              label=" "
              colon={false}
              style={{
                marginBottom: 24,
                fontWeight: 500,
                fontFamily: 'Roboto',
              }}
            >
              <div className="ends-label">
                <FormattedMessage {...messages.ends} /> &nbsp;
              </div>
              <WorkingHoursTimePicker
                valueText="workingHoursEndTime"
                getFieldDecorator={form.getFieldDecorator}
                configTime={configEndTime}
              />
            </FormItem>
          </div>
          {preview && <div data-testid="zone-change-preview">{preview}</div>}
          {saveError && <div role="alert" aria-live="polite">{saveError}{zoneConflict && <Button onClick={() => window.location.reload()}>Reload</Button>}</div>}
          <div className="settings-slot-duration">
            <FormItem
              label={<FormattedMessage {...messages.slotDuration} />}
              colon={false}
              style={{
                marginBottom: 24,
                fontWeight: 500,
                fontFamily: 'Roboto',
              }}
            >
              <TimeSpan
                slotDuration={userSettings.SlotDuration}
                defaultDuration={30}
                getFieldDecorator={form.getFieldDecorator}
                timeSlotText="defaultTimeSlot"
                timeUnitText="defaultTimeSlotUnit"
              />
            </FormItem>
          </div>

          <div className="reminder-timespan">
            <FormItem
              label={<FormattedMessage {...messages.defaultReminderTime} />}
              colon={false}
              style={{
                marginBottom: 24,
                fontWeight: 500,
                fontFamily: 'Roboto',
              }}
            >
              <TimeSpan
                slotDuration={userSettings.DefaultReminderTime}
                defaultDuration={15}
                getFieldDecorator={form.getFieldDecorator}
                timeSlotText="defaultReminderTime"
                timeUnitText="defaultReminderTimeUnit"
              />
            </FormItem>
          </div>

          <div className="buffer-timespan">
            <FormItem
              label={<FormattedMessage {...messages.bufferDuration} />}
              colon={false}
              style={{
                marginBottom: 24,
                fontWeight: 500,
                fontFamily: 'Roboto',
              }}
            >
              <TimeSpan
                slotDuration={userSettings.BufferDuration}
                defaultDuration={0}
                getFieldDecorator={form.getFieldDecorator}
                timeSlotText="bufferDuration"
                timeUnitText="bufferDurationUnit"
              />
            </FormItem>
          </div>
        </Form>
      </Modal>
    </div>
  );
}

CalendarSettingsComponent.propTypes = {
  form: PropTypes.shape({
    validateFields: PropTypes.func,
    getFieldDecorator: PropTypes.func,
    getFieldValue: PropTypes.func,
  }).isRequired,
  handleCancel: PropTypes.func.isRequired,
  handleOk: PropTypes.func.isRequired,
  showModal: PropTypes.func.isRequired,
  visible: PropTypes.bool.isRequired,
  showTrigger: PropTypes.bool,
  openProvider: PropTypes.func.isRequired,
  revokeAccess: PropTypes.func.isRequired,
  refreshCalendar: PropTypes.func.isRequired,
  userSettings: PropTypes.shape({
    AvailableWeekDays: PropTypes.arrayOf(PropTypes.string),
    StartingFreeTime: PropTypes.string,
    EndingFreeTime: PropTypes.string,
    SlotDuration: PropTypes.number,
    DefaultReminderTime: PropTypes.number,
    BufferDuration: PropTypes.number,
    TimeZone: PropTypes.string,
    TimeZoneIds: PropTypes.arrayOf(PropTypes.string),
    LocalStartingFreeTime: PropTypes.string,
    LocalEndingFreeTime: PropTypes.string,
    HoursAreDefault: PropTypes.bool,
  }).isRequired,
  adoptedZone: PropTypes.string,
  onAdoptZone: PropTypes.func,
  onChooseZone: PropTypes.func,
  zoneSelectRef: PropTypes.func,
  triggerRef: PropTypes.func,
  saving: PropTypes.bool,
  saveError: PropTypes.string,
  saveStatus: PropTypes.string,
  zoneConflict: PropTypes.bool,
  zoneRequired: PropTypes.bool,
  fetchSettings: PropTypes.func,
  settingsLoadFailed: PropTypes.bool,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }).isRequired,
  externalProviders: PropTypes.shape({
    google: PropTypes.shape({
      ProviderName: PropTypes.string,
      Registered: PropTypes.bool,
      Url: PropTypes.string,
    }),
    outlook: PropTypes.shape({
      ProviderName: PropTypes.string,
      Registered: PropTypes.bool,
      Url: PropTypes.string,
    }),
  }).isRequired,
};

CalendarSettingsComponent.defaultProps = {
  showTrigger: true,
  adoptedZone: null,
  onAdoptZone: () => {},
  onChooseZone: () => {},
  zoneSelectRef: () => {},
  triggerRef: () => {},
  saving: false,
  saveError: '',
  saveStatus: '',
  zoneConflict: false,
  zoneRequired: false,
  fetchSettings: () => {},
  settingsLoadFailed: false,
};

export { CalendarSettingsComponent as UnwrappedCalendarSettingsComponent };
export default injectIntl(CalendarSettingsComponent);
